import React, { useState } from 'react';
import { Typography, Box, IconButton, Tooltip, Grid, Link } from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import CustomBasicCard from 'src/App/Dashboards/Market/Components/Cards/CustomBasicCard';
import CodeIcon from '@mui/icons-material/Code';
import { COLOR_GREY_600, COLOR_PRIMARY_1, COLOR_PRIMARY_2 } from 'src/utils/colors';

interface CodeExampleCardProps {
  resourceLinks: ResourceLink[];
  codeExample: string;
}

interface ResourceLink {
  text: string;
  href: string;
  external?: boolean;
}

const CodeExampleCard: React.FC<CodeExampleCardProps> = ({ resourceLinks, codeExample }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codeExample);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  const content = (
    <>
      <Grid item xs={12}>
        <Typography variant="body1" sx={{ color: COLOR_GREY_600, mb: 2 }}>
          Example Request
        </Typography>

        <Box
          sx={{
            position: 'relative',
            backgroundColor: 'grey.100',
            borderRadius: 1,
            p: 2,
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            lineHeight: 1.6,
            overflow: 'auto',
            border: '1px solid',
            borderColor: 'grey.300',
            mb: 2,
          }}
        >
          <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
            <IconButton
              onClick={handleCopy}
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: 'white',
                '&:hover': {
                  backgroundColor: 'grey.100',
                },
              }}
            >
              <ContentCopy fontSize="small" />
            </IconButton>
          </Tooltip>

          <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
            <code>{codeExample}</code>
          </pre>
        </Box>

        <Typography variant="body1" sx={{ color: COLOR_GREY_600, mb: 2 }}>
          Resources
        </Typography>

        <Box
          component="ul"
          sx={{
            pl: 2,
            m: 0,
            listStyleType: 'disc',
            '& li': {
              listStyleType: 'disc',
              display: 'list-item',
            },
          }}
        >
          {resourceLinks.map((link, index) => (
            <Box
              component="li"
              key={index}
              sx={{
                mb: 0.5,
                listStyleType: 'disc',
                display: 'list-item',
              }}
            >
              <Link
                href={link.href}
                target={link.external ? '_blank' : undefined}
                rel={link.external ? 'noopener noreferrer' : undefined}
                sx={{
                  color: COLOR_PRIMARY_1,
                  textDecoration: 'none',
                  fontWeight: 500,
                  fontSize: '14px',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {link.text}
              </Link>
            </Box>
          ))}
        </Box>
      </Grid>
    </>
  );

  return <CustomBasicCard title="Quick Start Guide" icon={<CodeIcon />} content={content} />;
};

export default CodeExampleCard;
